import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, UNSAFE_NavigationContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Store scroll position outside component to survive re-renders
let savedNavScroll = 0;

const NAV = [
  { section: 'OVERVIEW' },
  { label: 'Dashboard',      path: '/' },
  { section: 'OPERATIONS' },
  { label: 'Work Sessions',  path: '/operations' },
  { label: 'Machines',       path: '/machines' },
  { section: 'PRODUCTION' },
  { label: 'Blocks',         path: '/production/blocks' },
  { label: 'Stock',          path: '/production/stock' },
  { section: 'BUSINESS' },
  { label: 'Sales',          path: '/sales' },
  { label: 'Expense',        path: '/expenses'},
  {
    label: 'Purchase',
    path: '/purchase',
    children: [
      { label: 'Purchase Orders', path: '/purchase' },
      { label: 'Item Master',     path: '/purchase/items' },
    ]
  },
  { label: 'Assets', path: '/assets' },
  { section: 'HR' },
  { label: 'Employees',      path: '/employees' },
  { label: 'Attendance',     path: '/attendance' },
  { section: 'REPORTS' },
  {
    label: 'Reports',
    path: '/reports',
    children: [
      { label: 'Daily Report',      path: '/reports/daily' },
      { label: 'Work Report',       path: '/reports/work' },
      { label: 'Production Report', path: '/reports/production' },
      { label: 'Expense Report',    path: '/reports/expenses' },
      { label: 'Diesel Report', path: '/reports/diesel'},
    ]
  },
  { section: 'SYSTEM' },
  { label: 'Settings',       path: '/settings' },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [open, setOpen]       = useState(false);
  const [expanded, setExpanded] = useState({});
  const navRef = useRef(null);
  const scrollPos = useRef(0);

  const isReportPath   = location.pathname.startsWith('/reports');
  const isPurchasePath = location.pathname.startsWith('/purchase');

  // Auto-expand submenu based on current path
  useEffect(() => {
    if(isReportPath)   setExpanded(e => ({...e, '/reports': true}));
    if(isPurchasePath) setExpanded(e => ({...e, '/purchase': true}));
  }, [isReportPath, isPurchasePath]);

  useEffect(() => {
    if(navRef.current) {
      navRef.current.style.visibility = 'hidden';
      navRef.current.scrollTop = savedNavScroll;
      navRef.current.style.visibility = 'visible';
    }
  });

  const handleNav = path => {
    savedNavScroll = navRef.current?.scrollTop || 0;
    navigate(path, { preventScrollReset: true });
    setOpen(false);
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const toggleExpand = key => setExpanded(e => ({ ...e, [key]: !e[key] }));

  const initials = user?.full_name
    ? user.full_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()
    : 'A';

  return (
    <>
      <button className="hamburger" onClick={() => setOpen(!open)}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {open
            ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
            : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
          }
        </svg>
      </button>

      <div className={`sidebar-overlay ${open ? 'open' : ''}`} onClick={() => setOpen(false)}/>

      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="logo-mark">
            <img src="/tyche_whitelogo.png" alt="Tyche Abodes" style={{width:75, height:40, objectFit:'contain', borderRadius:6}} onError={e=>e.target.style.display='none'}/>
            <div className="logo-text">
              <h2>Tyche Abodes</h2>
              <p>Granite ERP</p>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav" ref={navRef}>
          {NAV.map((item, i) => {
            if (item.section) return (
              <div key={i} className="nav-section-label">{item.section}</div>
            );

            // Item with children (sub-menu)
            if (item.children) {
              const isExpanded = expanded[item.path] !== undefined
                ? expanded[item.path]
                : (item.path === '/reports' ? isReportPath : isPurchasePath);
              const isActive = item.children.some(c => location.pathname === c.path);

              return (
                <div key={i}>
                  <div
                    className={`nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => toggleExpand(item.path)}
                    style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <div className="nav-dot"/>
                      {item.label}
                    </div>
                    <span style={{ fontSize:10, marginRight:4, color:'var(--text-muted)', transition:'transform 0.2s', transform:isExpanded?'rotate(180deg)':'rotate(0deg)', display:'inline-block' }}>▼</span>
                  </div>

                  {isExpanded && (
                    <div style={{ paddingLeft:16, borderLeft:'2px solid var(--border)', marginLeft:20, marginBottom:4 }}>
                      {item.children.map((child, ci) => {
                        const childActive = location.pathname === child.path;
                        return (
                          <div
                            key={ci}
                            onClick={() => handleNav(child.path)}
                            style={{
                              padding:'7px 12px', fontSize:12,
                              fontWeight: childActive ? 600 : 400,
                              color: childActive ? 'var(--brand)' : 'var(--text-secondary)',
                              cursor:'pointer', borderRadius:'var(--radius-sm)',
                              background: childActive ? 'var(--brand-dim)' : 'transparent',
                              marginBottom:2, display:'flex', alignItems:'center', gap:8, transition:'all 0.15s',
                            }}
                            onMouseEnter={e => { if(!childActive) e.currentTarget.style.background='var(--bg-hover)'; }}
                            onMouseLeave={e => { if(!childActive) e.currentTarget.style.background='transparent'; }}
                          >
                            <div style={{ width:4, height:4, borderRadius:'50%', background:childActive?'var(--brand)':'var(--border)', flexShrink:0 }}/>
                            {child.label}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            // Regular item
            const isActive = location.pathname === item.path;
            return (
              <div key={i} className={`nav-item ${isActive ? 'active' : ''}`}
                onClick={() => handleNav(item.path)}>
                <div className="nav-dot"/>
                {item.label}
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-card">
            <div className="user-avatar">{initials}</div>
            <div className="user-info">
              <div className="name">{user?.full_name || 'Admin'}</div>
              <div className="role">{user?.role || 'ADMIN'}</div>
            </div>
            <button className="logout-btn" onClick={handleLogout} title="Logout">
              ⏻
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}